import { useEffect, useState } from 'react';
import { ContactFields, isAuthError, useAuth0 } from '@brighte/passwordless-authenticator';
import { ApplicantType } from '@brighte/validation';
import { Loader } from '@components/Loader/Loader';
import PageTitle from '@components/PageTitle/PageTitle';
import { datadogRum } from '@datadog/browser-rum';
import {
  ApplicationDocument,
  PropertyTitleOwnerOutput,
  PropertyTitleSearchAddressInput,
  TitleOwnerIncomeType,
  TitleOwnerVerificationMethod,
  TitleOwnerVerificationStatus,
  useGetTitleSearchOwnerQuery,
  useHandlePostApplicationLoginMutation,
  useReferralQuery,
  useSubmitTitleSearchOwnerMutation,
  useUpdateTitleSearchOwnerMutation,
} from '@graphql/index';
import { useScrollToTop } from '@hooks';
import { useApplicationData, useSetApplicationData } from '@provider';
import { getApplicantTypeAndTrackingIdFromUrl, isJointApplicant } from '@utils';
import ConfirmInstallationAddress from './ConfirmInstallationAddress';
import DeclareTaxableIncome from './DeclareTaxableIncome';
import PropertyTitleCheck from './PropertyTitleCheck';
import TitleOwnerResults from './TitleOwnerResults';
import TitleOwnerTerms from './TitleOwnerTerms';
import VerifyDeclaredIncome from './VerifyDeclaredIncome';

type LandTitleStep = 'terms' | 'address' | 'titleCheck' | 'titleOwnerResults' | 'declareIncome' | 'verifyIncome';

type LandTitleEligibilityCheckProps = {
  onComplete: () => void;
};

function LandTitleEligibilityCheck({ onComplete }: LandTitleEligibilityCheckProps) {
  const [step, setStep] = useState<LandTitleStep>('terms');
  const [titleOwners, setTitleOwners] = useState<PropertyTitleOwnerOutput[]>([]);
  const [declaredIncomes, setDeclaredIncomes] = useState<Record<string, string>>({});
  const [declaredIncomeTypes, setDeclaredIncomeTypes] = useState<Record<string, string>>({});
  const prePopContactDetails = useApplicationData('ContactDetails');
  const { quote } = useApplicationData(['quote']);
  const setApplicationData = useSetApplicationData();
  const { verificationCompleted } = useAuth0();
  const { trackingId } = getApplicantTypeAndTrackingIdFromUrl();
  const joint = isJointApplicant();
  const { mutateAsync: submitIncome } = useSubmitTitleSearchOwnerMutation({ useErrorBoundary: false });
  const { mutateAsync: saveDeclaredIncome } = useUpdateTitleSearchOwnerMutation({ useErrorBoundary: false });
  const { mutate: handlePostApplicationLogin } = useHandlePostApplicationLoginMutation();

  const { data: referral, isLoading } = useReferralQuery(
    { id: trackingId, joint },
    {
      enabled: verificationCompleted,
      select: data => data.referral,
      retry: 0,
      useErrorBoundary: (error: unknown) => isAuthError(error),
    },
  );

  const { data: existingTitleData, isLoading: isExistingOwnersLoading } = useGetTitleSearchOwnerQuery(
    { input: { applicationId: trackingId } },
    {
      enabled: verificationCompleted && step === 'terms',
      select: data => data.getTitleSearchOwner,
      retry: 0,
      useErrorBoundary: (error: unknown) => isAuthError(error),
    },
  );

  const existingOwners = existingTitleData?.owners;

  const isIncomeVerificationAlreadyComplete =
    existingTitleData?.ownershipDisputed === true ||
    (!!existingOwners?.length &&
      existingOwners.every(owner => {
        const normallyVerified =
          owner.aiIncomeCalculated != null && owner.declaredIncome != null && (owner.documents?.length ?? 0) > 0;
        // isIncomeVerified is null until submitTitleSearchOwner runs; once set it is the
        // authoritative completion signal, including paths that carry no NOA/documents:
        //   true  → verified (e.g. a pensioner verified via IDMatrix / concession card ACCEPT)
        //   false → $0 income or exhausted-retry manual-review bypass
        const submitted = owner.isIncomeVerified != null && owner.declaredIncome != null;
        return normallyVerified || submitted;
      }));

  useEffect(() => {
    if (isIncomeVerificationAlreadyComplete && step === 'terms') {
      onComplete();
    }
  }, [isIncomeVerificationAlreadyComplete, step]);

  useEffect(() => {
    if (step === 'terms' && !isIncomeVerificationAlreadyComplete && existingOwners?.some(owner => owner.isManual)) {
      setTitleOwners(existingOwners as PropertyTitleOwnerOutput[]);
      setDeclaredIncomes(
        Object.fromEntries(
          existingOwners.map(owner => [
            owner.id,
            owner.declaredIncome == null ? '' : String(owner.declaredIncome * 12),
          ]),
        ),
      );
      setDeclaredIncomeTypes(Object.fromEntries(existingOwners.map(owner => [owner.id, owner.incomeType ?? ''])));
      setStep('declareIncome');
    }
  }, [existingOwners, isIncomeVerificationAlreadyComplete, step]);

  // Step navigation here is state-driven (not route-based), so nothing resets scroll on
  // continue/back — people were landing mid-form and missing the top. Reset on step change.
  useScrollToTop([step]);

  const handleLoginError = (error: unknown) => {
    if (isAuthError(error)) {
      throw error;
    }
  };

  // Keeps titleOwners in sync so a remount (e.g. navigating back then forward) doesn't
  // resurrect an already-deleted document or a verdict the deletion invalidated.
  const handleOwnerDocumentDeleted = (ownerId: string, documentId: string, invalidatesVerification: boolean) => {
    setTitleOwners(previous =>
      previous.map(owner =>
        owner.id === ownerId
          ? {
              ...owner,
              documents: (owner.documents ?? []).filter(document => document.id !== documentId),
              verificationStatus: invalidatesVerification ? null : owner.verificationStatus,
            }
          : owner,
      ),
    );
  };

  // Keeps titleOwners in sync so a remount doesn't lose a same-session verification result.
  const handleOwnerVerified = (
    ownerId: string,
    verificationMethod: TitleOwnerVerificationMethod,
    documents: Array<{ id: string; filename: string }>,
  ) => {
    setTitleOwners(previous =>
      previous.map(owner => {
        if (owner.id !== ownerId) return owner;
        const existingDocuments = owner.documents ?? [];
        const existingIds = new Set(existingDocuments.map(document => document.id));
        // Only id/filename are read back from the snapshot (OwnerVerificationCard seeds its list from them).
        const addedDocuments = documents.filter(document => !existingIds.has(document.id)) as ApplicationDocument[];
        return {
          ...owner,
          documents: [...existingDocuments, ...addedDocuments],
          verificationMethod,
          verificationStatus: TitleOwnerVerificationStatus.VERIFIED,
        };
      }),
    );
  };

  const handleVerificationSuccess = ({
    verifiedEmail,
    verifiedMobile,
  }: {
    verifiedEmail?: string;
    verifiedMobile: string;
  }) => {
    setApplicationData({ ContactDetails: { email: verifiedEmail, mobile: verifiedMobile } });
    handlePostApplicationLogin({
      postLoginInput: {
        applicantType: joint ? ApplicantType.Joint : ApplicantType.Primary,
        opportunityId: quote?.opportunity?.id,
        trackingId,
      },
    });
  };

  if (!verificationCompleted) {
    return (
      <>
        <PageTitle />
        <ContactFields
          verificationDetails={{
            email: prePopContactDetails?.email,
            mobile: prePopContactDetails?.mobile,
            verificationType: prePopContactDetails?.verificationType,
            maskedEmail: prePopContactDetails?.maskedEmail,
            maskedMobile: prePopContactDetails?.maskedMobile,
          }}
          onError={handleLoginError}
          onSuccess={handleVerificationSuccess}
          descriptionAlign="center"
        />
      </>
    );
  }

  if (isLoading || isExistingOwnersLoading || isIncomeVerificationAlreadyComplete) {
    return <Loader />;
  }

  const addr = referral?.address;
  const productName = referral?.financialProduct?.name ?? '';
  const vendorName = referral?.vendor?.tradingName ?? '';
  const applicantName = [referral?.customer?.firstName, referral?.customer?.lastName].filter(Boolean).join(' ');
  const formattedAddress = addr
    ? [
        addr.unitNumber
          ? `${addr.unitNumber}/${addr.streetNumber} ${addr.streetName}`
          : `${addr.streetNumber} ${addr.streetName}`,
        `${addr.suburb} ${addr.state} ${addr.postcode}`,
      ].join(', ')
    : '';

  const rawAddress: PropertyTitleSearchAddressInput | null = addr
    ? {
        unitNumber: addr.unitNumber || undefined,
        streetNumber: addr.streetNumber,
        streetName: addr.streetName,
        suburb: addr.suburb,
        state: addr.state,
        postcode: addr.postcode,
      }
    : null;

  if (step === 'verifyIncome') {
    return (
      <VerifyDeclaredIncome
        owners={titleOwners}
        declaredIncomes={declaredIncomes}
        incomeTypes={declaredIncomeTypes}
        trackingId={trackingId}
        productName={productName}
        applicantName={applicantName}
        onDocumentDeleted={handleOwnerDocumentDeleted}
        onVerified={handleOwnerVerified}
        onConfirm={() => {
          datadogRum.addAction('LandOwner Income Verified', {
            trackingId,
            ownerCount: titleOwners.length,
          });
          onComplete();
        }}
        onBack={() => setStep('declareIncome')}
      />
    );
  }

  if (step === 'titleOwnerResults') {
    const handleTitleDisputed = async () => {
      datadogRum.addAction('LandOwner Title Disputed', { trackingId });
      try {
        await submitIncome({ input: { applicationId: trackingId, ownershipDisputed: true, owners: [] } });
      } catch {
        datadogRum.addAction('LandOwner Title Disputed Submit Error', { trackingId });
      }
      onComplete();
    };

    return (
      <TitleOwnerResults
        owners={titleOwners}
        applicantName={applicantName}
        productName={productName}
        onConfirm={() => {
          datadogRum.addAction('LandOwner Title Confirmed', {
            trackingId,
            ownerCount: titleOwners.length,
          });
          setStep('declareIncome');
        }}
        onTitleDisputed={handleTitleDisputed}
        onSelectionChange={selection => {
          datadogRum.addAction(
            selection === 'yes' ? 'LandOwner Title Owners Confirmed' : 'LandOwner Title Owners Disputed',
            { trackingId },
          );
        }}
      />
    );
  }

  if (step === 'declareIncome') {
    return (
      <DeclareTaxableIncome
        owners={titleOwners}
        productName={productName}
        initialIncomes={declaredIncomes}
        initialIncomeTypes={declaredIncomeTypes}
        onConfirm={async (incomes, incomeTypes) => {
          datadogRum.addAction('LandOwner Income Declared', {
            trackingId,
            ownerCount: titleOwners.length,
          });
          setDeclaredIncomes(incomes);
          setDeclaredIncomeTypes(incomeTypes);
          // Step 1 save-and-continue: persist declared income + type per owner.
          // declaredIncome is stored monthly (BE convention), same as the submit payload.
          try {
            await saveDeclaredIncome({
              input: {
                applicationId: trackingId,
                owners: titleOwners.map(owner => ({
                  id: owner.id,
                  declaredIncome: Number(incomes[owner.id] ?? 0) / 12,
                  incomeType: incomeTypes[owner.id] as TitleOwnerIncomeType,
                })),
              },
            });
          } catch {
            datadogRum.addAction('LandOwner Income Declare Save Error', { trackingId });
          }
          setStep('verifyIncome');
        }}
        onBack={() => setStep(titleOwners.some(owner => owner.isManual) ? 'titleCheck' : 'titleOwnerResults')}
      />
    );
  }

  if (step === 'titleCheck' && rawAddress) {
    return (
      <PropertyTitleCheck
        address={formattedAddress}
        rawAddress={rawAddress}
        trackingId={trackingId}
        productName={productName}
        onContinue={owners => {
          datadogRum.addAction('LandOwner Title Check Complete', {
            trackingId,
            ownerCount: owners.length,
          });
          setTitleOwners(owners);
          setStep(owners.some(owner => owner.isManual) ? 'declareIncome' : 'titleOwnerResults');
        }}
        declaredOwners={titleOwners.filter(owner => owner.isManual)}
        onContinueToApplication={() => {
          datadogRum.addAction('LandOwner Title Check Unavailable Continue', { trackingId });
          onComplete();
        }}
        onBack={() => setStep('address')}
      />
    );
  }

  if (step === 'address') {
    return (
      <ConfirmInstallationAddress
        address={formattedAddress}
        productName={productName}
        vendorName={vendorName}
        onConfirm={() => {
          datadogRum.addAction('LandOwner Address Confirmed', { trackingId });
          setStep('titleCheck');
        }}
        onBack={() => setStep('terms')}
        onNotCorrectAddress={() => datadogRum.addAction('LandOwner Address Not Correct Opened', { trackingId })}
      />
    );
  }

  return (
    <TitleOwnerTerms
      productName={productName}
      onContinue={() => {
        datadogRum.addAction('LandOwner Terms Accepted', { trackingId });
        setStep('address');
      }}
    />
  );
}

export default LandTitleEligibilityCheck;
